import { createFileRoute } from "@tanstack/react-router";
import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";
import { FraudQueueTable } from "@/components/fraud/fraud-queue-table";
import { FraudStats } from "@/components/fraud/fraud-stats";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { IconRefresh, IconDownload } from "@tabler/icons-react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/lib/constants";
import { exportFraudQueue } from "@/hooks/use-fraud";
import { toast } from "sonner";

export const Route = createFileRoute("/fraud/")({
  component: FraudDetectionPage,
});

function FraudDetectionPage() {
  return (
    <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
      <AppShell>
        <FraudDetectionContent />
      </AppShell>
    </ProtectedRoute>
  );
}

function FraudDetectionContent() {
  const queryClient = useQueryClient();
  const [riskFilter, setRiskFilter] = useState("all");
  const page = 1;
  const pageSize = 20;

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FRAUD_QUEUE });
    toast.success("Refreshed", {
      description: "Fraud queue has been refreshed.",
    });
  };

  const handleExport = async () => {
    const toastId = "export-fraud-queue";
    toast.loading("Exporting Fraud Queue", { id: toastId });

    try {
      await exportFraudQueue(
        {
          risk_level:
            riskFilter === "all" ? undefined : (riskFilter as any),
        },
        "csv",
      );

      toast.success("Export Complete", {
        id: toastId,
        description: "Fraud queue has been exported successfully.",
      });
    } catch (error) {
      toast.error("Export Failed", {
        id: toastId,
        description:
          error instanceof Error
            ? error.message
            : "Failed to export fraud queue.",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Fraud Detection</h1>
          <p className="text-muted-foreground">
            Monitor and manage flagged transactions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <IconRefresh className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <IconDownload className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      <FraudStats />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Fraud Queue</CardTitle>
              <CardDescription>
                Transactions flagged by the fraud detection system
              </CardDescription>
            </div>
            <Select value={riskFilter} onValueChange={setRiskFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Risk Levels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risk Levels</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <FraudQueueTable
            riskFilter={riskFilter}
            page={page}
            pageSize={pageSize}
          />
        </CardContent>
      </Card>
    </div>
  );
}
